/**
 * 获取 URL 中的查询参数
 * @param options - 配置项
 * @param options.params - 可选，指定要获取的单个参数名
 * @param options.url - 可选，目标 URL，默认取当前页面 URL（浏览器环境）
 * @returns 若传了 params 则返回对应值（string | null），否则返回所有参数的键值对对象
 */
export function getUrlQueryParams(
  options: { params?: string; url?: string; } = {}
): string | null | Record<string, string> {
  if (typeof window === 'undefined') return null;
  // 处理 URL 默认值（兼容非浏览器环境）
  const { params, url = typeof location !== 'undefined' ? location.href : '' } = options;

  // 提取 URL 中的查询字符串（忽略哈希部分）
  const queryString = new URL(url, window?.location?.href).search;
  const searchParams = new URLSearchParams(queryString);

  // 若指定了参数名，返回对应值
  if (params) {
    return searchParams.get(params);
  }

  // 否则返回所有参数的键值对对象
  const paramsObj: Record<string, string> = {};
  for (const [key, value] of searchParams.entries()) {
    paramsObj[key] = value; // 若有重复参数，取最后一个值（URLSearchParams 特性）
  }

  return paramsObj;
}

/**
 * 设置/更新 URL 查询参数并返回新的 URL 字符串（不刷新页面）
 * - 默认在保留原有查询参数的基础上进行增量更新
 * - 传入 null/undefined 将移除该参数（可通过 options 关闭）
 * - 支持数组参数（会以多 key 形式追加：?k=a&k=b）
 */
export function setUrlQueryParams(
  urlOrParams: string | URL = typeof location !== 'undefined' ? location.href : '',
  params: Record<string, string | number | boolean | Array<string | number | boolean> | null | undefined> = {},
  options: {
    removeNullish?: boolean; // null/undefined 是否移除参数（默认 true）
    removeEmptyString?: boolean; // 空字符串是否移除参数（默认 false）
  } = {}
): string {
  const { removeNullish = true, removeEmptyString = false } = options;

  const base = typeof urlOrParams === 'string' ? urlOrParams : urlOrParams.toString();
  const u = new URL(base, typeof window !== 'undefined' ? window.location.href : undefined);
  const sp = u.searchParams;

  Object.entries(params).forEach(([key, value]) => {
    // 处理移除逻辑
    if (value == null) {
      if (removeNullish) sp.delete(key);
      return;
    }
    if (value === '' && removeEmptyString) {
      sp.delete(key);
      return;
    }

    // 统一转为数组处理（支持多值）
    const values = Array.isArray(value) ? value : [value];
    // 先清除旧值，再逐个添加新值
    sp.delete(key);
    values.forEach((v) => sp.append(key, String(v)));
  });

  // 返回标准化后的完整 URL 字符串
  u.search = sp.toString();
  return u.toString();
}

/**
 * 智能追加查询参数到 URL
 * - 如果 URL 中没有查询参数（没有 ?），则使用 ? 连接
 * - 如果 URL 中已有查询参数（已有 ?），则使用 & 连接
 * - 自动处理哈希（#）的情况
 * @param url - 基础 URL
 * @param params - 要追加的查询参数字符串（例如："source=website"）或对象（例如：{source: "website"}）
 * @returns 追加参数后的完整 URL
 * @example
 * appendQueryParam('https://example.com/path', 'source=website') // => 'https://example.com/path?source=website'
 * appendQueryParam('https://example.com/path?id=1', 'source=website') // => 'https://example.com/path?id=1&source=website'
 * appendQueryParam('https://example.com/path#hash', 'source=website') // => 'https://example.com/path?source=website#hash'
 */
export function appendQueryParam(
  url: string,
  params: string | Record<string, string | number | boolean>
): string {
  if (!params) return url;

  // 将 params 转换为查询字符串
  let queryString: string;
  if (typeof params === 'string') {
    queryString = params;
  } else {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value != null) {
        searchParams.append(key, String(value));
      }
    });
    queryString = searchParams.toString();
  }

  if (!queryString) return url;

  // 分离 URL 的哈希部分
  const hashIndex = url.indexOf('#');
  const hash = hashIndex !== -1 ? url.substring(hashIndex) : '';
  const urlWithoutHash = hashIndex !== -1 ? url.substring(0, hashIndex) : url;

  // 检查是否已有查询参数
  const hasQuery = urlWithoutHash.includes('?');
  const separator = hasQuery ? '&' : '?';

  return `${urlWithoutHash}${separator}${queryString}${hash}`;
}

/**
 * 从 URL 中移除指定的查询参数
 * @param url - 目标 URL
 * @param paramNames - 要移除的参数名（可以是单个字符串或字符串数组）
 * @returns 移除参数后的 URL
 * @example
 * removeQueryParam('https://example.com/path?inviteCode=123&source=website', 'inviteCode') // => 'https://example.com/path?source=website'
 * removeQueryParam('https://example.com/path?inviteCode=123&source=website', ['inviteCode', 'source']) // => 'https://example.com/path'
 */
export function removeQueryParam(
  url: string,
  paramNames: string | string[]
): string {
  const paramsToRemove = Array.isArray(paramNames) ? paramNames : [paramNames];
  if (paramsToRemove.length === 0) return url;

  try {
    const u = new URL(url, typeof window !== 'undefined' ? window.location.href : undefined);
    paramsToRemove.forEach(param => {
      u.searchParams.delete(param);
    });
    return u.toString();
  } catch {
    // 如果不是完整 URL，尝试手动处理
    const hashIndex = url.indexOf('#');
    const hash = hashIndex !== -1 ? url.substring(hashIndex) : '';
    const urlWithoutHash = hashIndex !== -1 ? url.substring(0, hashIndex) : url;

    const [base, queryString] = urlWithoutHash.split('?');
    if (!queryString) return url;

    const params = new URLSearchParams(queryString);
    paramsToRemove.forEach(param => {
      params.delete(param);
    });

    const newQueryString = params.toString();
    return newQueryString ? `${base}?${newQueryString}${hash}` : `${base}${hash}`;
  }
}

/**
 * 从完整 URL 中提取 .com 之后的所有路径，包括：
 *  - pathname  (/开头的路径)
 *  - search    (查询参数 ?id=xxx)
 *  - hash      (#锚点)
 *
 * 例如：
 *  https://web-test.cggame123.com/en/affiliate?id=88#top
 *  -> /en/affiliate?id=88#top
 * 若传入本身是 "/xxx" 形式路径，则原样返回
 */
export function getFullPathAfterCom(url: string) {
  // 去除可能的空格
  const cleanUrl = url.trim();

  // 如果是内部路径，直接返回，不进行 URL 解析
  if (cleanUrl.startsWith('/')) return cleanUrl;
  try {
    // 将传入字符串转成 URL 对象（自动解析协议、域名、端口、路径、查询参数等）
    const u = new URL(url);

    // 返回域名后的完整部分：路径 + 查询参数 + hash 锚点
    return u.pathname + u.search + u.hash;
  } catch {
    // 当 url 无效或解析失败时返回空字符串，不中断程序
    return '';
  }
}
